<template>
    <div class="overview-container">
        <alert :show-alert="showAlert" :message="alertMessage" :type="alertType" />
        <div v-if="getCurrentViberCampaign" class="info-container">
            <v-card class="table-wrapper">
                <campaign-table :rows="campaignTableRows">
                    <template v-slot:fallback_icon>
                        <two-state-icon :condition="getCurrentViberCampaign.fallback ?? false" class="fallback-icon" />
                    </template>
                    <template v-slot:file_used>
                        <v-btn
                            color="primary"
                            class="my-2 white--text"
                            @click="downloadFile"
                        >
                            Download
                            <v-icon right dark> mdi-file-download-outline </v-icon>
                        </v-btn>
                    </template>
                </campaign-table>
                <div>
                    <delete-dialog
                        v-if="hasDeletePrivilege"
                        :disable-delete="hasDeletePrivilege"
                        class="delete-dialog"
                        @delete-event="deleteViberCampaign()"
                    />
                </div>
            </v-card>
            <v-card v-if="messagesStatusCounts.length > 0" class="charts-container">
                <div class="chart-row">
                    <radial-bar :progress="progress" />
                    <donut-chart :status-counts="messagesStatusCounts" />
                    <simple-table :status-counts="messagesStatusCounts" />
                </div>
                <div
                    v-if="messagesFallbackStatusCounts.length > 0"
                    class="chart-row fallback"
                >
                    <donut-chart :status-counts="messagesFallbackStatusCounts" />
                    <simple-table :status-counts="messagesFallbackStatusCounts" />
                </div>
            </v-card>
        </div>
        <v-card class="table-container">
            <div class="card-title">
                <div>{{ viberTitle }}</div>
            </div>
            <array-view
                :title="viberTitle"
                :items="viberMessages"
                :headers="viberHeaders"
                :total-pages="viberMessagesTotalPages"
                :page="viberPage"
                :size="viberSize"
                :total-items="totalViberMessages"
                :has-search-field="true"
                :update-filter="updateViberFilter"
                :is-fetching="isFetchingViber"
                @paginationChange="viberPaginationChange"
            />
        </v-card>
        <v-card v-if="getCurrentViberCampaign.fallback" class="table-container">
            <div class="card-title">
                <div>{{ fallbackTitle }}</div>
            </div>

            <array-view
                :title="fallbackTitle"
                :items="fallbackMessages"
                :headers="fallbackHeaders"
                :total-pages="fallbackMessagesTotalPages"
                :page="fallbackPage"
                :size="fallbackSize"
                :has-search-field="true"
                :update-filter="updateFallbackFilter"
                :total-items="totalFallbackMessages"
                :is-fetching="isFetchingFallback"
                @paginationChange="fallbackPaginationChange"
            />
        </v-card>
    </div>
</template>

<script>
    import dateTimeUtils from '@/utils/date-time-utils'
    import viberCampaignService from '@/services/viber-campaign-service'
    import recurringViberCampaignService from '@/services/recurring-viber-campaign-service'
    import RadialBar from '@/components/RadialBar.vue'
    import DonutChart from '@/components/DonutChart.vue'
    import SimpleTable from '@/components/SimpleTable.vue'
    import ArrayView from '@/components/ArrayView.vue'
    import TwoStateIcon from '@/components/TwoStateIcon.vue'
    import CampaignTable from '@/components/CampaignTable.vue'
    import tableUtils from '@/utils/tableUtils'
    import { hasPrivilege } from '@/utils/userUtils'
    import { PRIVILEGES } from '@/utils/consts'
    import DeleteDialog from '@/components/DeleteDialog.vue'
    import Alert from '@/components/Alert.vue'
    export default {
        components: {
            CampaignTable,
            TwoStateIcon,
            RadialBar,
            DonutChart,
            SimpleTable,
            ArrayView,
            DeleteDialog,
            Alert
        },
        props: {
            campaign: {
                type: Object,
                required: false,
                default: null
            }
        },
        data() {
            return {
                showAlert: false,
                alertMessage: '',
                alertType: 'error',
                submitLoading: false,
                progress: 0,
                messagesStatusCounts: [],
                messagesFallbackStatusCounts: [],
                viberTitle: 'Viber Messages List',
                fallbackTitle: 'SMS Fallback List',
                viberHeaders: [
                    {
                        text: '#',
                        value: 'index'
                    },
                    { text: 'Mobile Number', value: 'recipient' },
                    { text: 'Text', value: 'viberText' },
                    { text: 'Date Sent', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Date of Status', value: 'statusDate' },
                    { text: '', value: 'data-table-expand' }
                ],
                fallbackHeaders: [
                    {
                        text: '#',
                        value: 'index'
                    },
                    { text: 'Mobile Number', value: 'recipient' },
                    { text: 'Text', value: 'smsText' },
                    { text: 'SMS Parts', value: 'parts' },
                    { text: 'Chars', value: 'chars' },
                    { text: 'Is UTF-8', value: 'utf8' },
                    { text: 'Date Sent', value: 'sentDate' },
                    { text: 'Status', value: 'status' },
                    { text: 'Date of Status', value: 'statusDate' },
                    { text: '', value: 'data-table-expand' }
                ],
                viberMessages: [],
                fallbackMessages: [],
                viberMessagesTotalPages: 0,
                fallbackMessagesTotalPages: 0,
                viberSize: 5,
                viberPage: 1,
                fallbackSize: 5,
                fallbackPage: 1,
                totalViberMessages: 0,
                totalFallbackMessages: 0,
                viberFilter: '',
                fallbackFilter: '',
                isFetchingViber: false,
                isFetchingFallback: false
            }
        },
        computed: {
            hasDeletePrivilege() {
                return hasPrivilege(
                    PRIVILEGES.RECURRING_VIBER_CAMPAIGN_DELETE_PRIVILEGE,
                    this.$store.getters.getCurrentUserPrivileges
                )
            },
            campaignTableRows() {
                return [
                    {
                        header: 'Id :',
                        value: this.getCurrentViberCampaign.id
                    },
                    {
                        header: 'Campaign :',
                        value: this.getCurrentViberCampaign.alias
                    },
                    {
                        header: 'Originator :',
                        value: this.getCurrentViberCampaign.originator
                    },
                    {
                        header: 'Text Template :',
                        value: this.getCurrentViberCampaign.textTemplate
                    },
                    {
                        header: 'Alias :',
                        value: this.getCurrentViberCampaign.alias
                    },
                    {
                        header: 'TTL :',
                        value: this.getCurrentViberCampaign.ttl
                    },
                    {
                        header: 'SMS Fallback :',
                        type: 'slot',
                        slot: 'fallback_icon'
                    },
                    {
                        header: 'SMS Fallback Text Template :',
                        value: this.getCurrentViberCampaign.fallbackTextTemplate ?? '-'
                    },
                    {
                        header: 'Button text :',
                        value: this.getCurrentViberCampaign.buttonCaption ?? '-'
                    },
                    {
                        header: 'Button Url :',
                        value: this.getCurrentViberCampaign.buttonUrl ?? '-'
                    },
                    {
                        header: 'Image Url :',
                        value: this.getCurrentViberCampaign.imageUrl ?? '-'
                    },
                    {
                        header: 'File Used :',
                        type: 'slot',
                        renderCondition: !!this.getCurrentViberCampaign.file,
                        slot: 'file_used'
                    },
                    {
                        header: 'Created At :',
                        value: this.getHumanizedDateTime(
                            this.getCurrentViberCampaign.createdAt
                        )
                    },
                    {
                        header: 'Scheduled At :',
                        value: this.getHumanizedDateTime(
                            this.getCurrentViberCampaign.scheduledAt
                        )
                    },
                    {
                        header: 'Parameters :',
                        value: this.getCurrentViberCampaign.params
                    }
                ]
            },
            getCurrentViberCampaign() {
                return this.campaign
                    ? this.campaign
                    : this.$store.getters.getCurrentRecurringViberCampaign
            },
            isDetailsRoute() {
                return this.$route.params.campaignId
            },
            filter() {
                return {
                    filter: { q: this.viberFilter },
                    range: [this.viberPage - 1, this.viberSize]
                }
            },
            fallbackMessagesFilter() {
                return {
                    filter: { q: this.fallbackFilter },
                    range: [this.fallbackPage - 1, this.fallbackSize]
                }
            }
        },
        watch: {
            getCurrentViberCampaign() {
                this.initDetails()
            },
            filter() {
                this.fetchAllViberMessages()
            },
            fallbackMessagesFilter() {
                this.fetchAllFallbackMessages()
            },
            showAlert: {
                handler(newval) {
                    if (newval) {
                        setTimeout(() => { this.showAlert = false }, 3000)
                    }
                }
            }
        },
        created() {
            this.initDetails()
        },
        methods: {
            initDetails() {
                if (this.getCurrentViberCampaign.id) {
                    recurringViberCampaignService
                        .getMessages(this.getCurrentViberCampaign.id, this.filter)
                        .then((resp) => {
                            this.viberMessages = resp.data.content.map((campaign, index) => ({
                                ...campaign,
                                index: index + (this.viberPage - 1) * this.viberSize + 1
                            }))
                            this.viberMessagesTotalPages = resp.data.totalPages
                            this.totalViberMessages = resp.data.totalElements
                        })
                        .catch((error) => {
                            this.setAlert(error.response.data.message, 'error')
                        })
                    recurringViberCampaignService
                        .getFallbacks(
                            this.getCurrentViberCampaign.id,
                            this.fallbackMessagesFilter
                        )
                        .then((resp) => {
                            this.fallbackMessages = resp.data.content.map(
                                (campaign, index) => ({
                                    ...campaign,
                                    index: index + (this.fallbackPage - 1) * this.fallbackSize + 1
                                })
                            )
                            this.fallbackMessagesTotalPages = resp.data.totalPages
                            this.totalFallbackMessages = resp.data.totalElements
                        })
                        .catch((error) => {
                            this.setAlert(error.response.data.message, 'error')
                        })
                }
            },
            getHumanizedDateTime(dateTime) {
                return dateTimeUtils.getHumanizedDateTime(dateTime)
            },
            viberPaginationChange(page, size) {
                this.viberSize = size
                this.viberPage = page
            },
            fallbackPaginationChange(page, size) {
                this.fallbackSize = size
                this.fallbackPage = page
            },
            fetchAllViberMessages() {
                this.isFetchingViber = true
                return recurringViberCampaignService
                    .getMessages(this.getCurrentViberCampaign.id, this.filter)
                    .then((resp) => {
                        this.viberMessages = resp.data.content.map((message, index) => ({
                            ...message,
                            index: index + (this.viberPage - 1) * this.viberSize + 1,
                            details: this.getMessageDetails(message)
                        }))
                        this.viberMessagesTotalPages = resp.data.totalPages
                        this.totalViberMessages = resp.data.totalElements
                    })
                    .catch((error) => {
                        this.setAlert(error.response.data.message, 'error')
                    })
                    .finally(() => {
                        this.isFetchingViber = false
                    })
            },
            fetchAllFallbackMessages() {
                this.isFetchingFallback = true
                return recurringViberCampaignService
                    .getFallbacks(
                        this.getCurrentViberCampaign.id,
                        this.fallbackMessagesFilter
                    )
                    .then((resp) => {
                        this.fallbackMessages = resp.data.content.map((message, index) => ({
                            ...message,
                            index: index + (this.fallbackPage - 1) * this.fallbackSize + 1,
                            details: this.getMessageDetails(message)
                        }))
                        this.fallbackMessagesTotalPages = resp.data.totalPages
                        this.totalFallbackMessages = resp.data.totalElements
                    })
                    .catch((error) => {
                        this.setAlert(error.response.data.message, 'error')
                    })
                    .finally(() => {
                        this.isFetchingFallback = false
                    })
            },
            downloadFile() {
                viberCampaignService
                    .downloadUploadedFile(this.getCurrentViberCampaign.id)
                    .then((resp) => {
                        this.exportCsvFile(
                            this.getCurrentViberCampaign.alias + '_file_used',
                            resp.data
                        )
                    })
                    .catch((error) => {
                        this.setAlert(error.response.data.message, 'error')
                    })
            },
            exportCsvFile(fileName, fileData) {
                const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), fileData], {
                    type: 'text/csv;charset=utf-8;'
                })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(blob)
                link.download = `${fileName}.csv`

                const container = document.getElementById('export')
                container.appendChild(link)
                link.click()
            },
            updateViberFilter(newFilter) {
                this.viberFilter = newFilter
                this.viberPage = 1
            },
            updateFallbackFilter(newFilter) {
                this.fallbackFilter = newFilter
                this.fallbackPage = 1
            },
            getMessageDetails(message) {
                return tableUtils.getMessageDetails(message)
            },
            deleteViberCampaign() {
                recurringViberCampaignService.deleteViberCampaign(this.getCurrentViberCampaign.id).then(() => {
                    this.setAlert('Recurring Viber Campaign Successfully Deleted!', 'success')
                    setTimeout(() => {
                        this.$router.push('/recurring-viber')
                    }, 1000)
                }).catch(error => {
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            setAlert(message, type) {
                this.showAlert = true
                this.alertMessage = message
                this.alertType = type
            }
        }
    }
</script>

<style scoped>
.overview-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.info-container {
    display: flex;
    column-gap: 10px;
}

.table-container {
    flex-grow: 1;
}

.card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
}

.charts-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.chart-row {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 20px;
}

.chart-row.fallback {
    justify-content: flex-end;
}

.export-btn {
    text-transform: none !important;
    background: #00a65a !important;
    color: white;
    margin: 15px auto;
    width: 150px;
}

.fallback-icon {
    text-align: center;
    padding: 5px;
    width: 50px;
}

.table-wrapper {
    padding-bottom: 1rem;
    width: 100%;
}

.delete-dialog {
    display: flex;
    justify-content: flex-end;
    padding: 0.5rem;
}

@media screen and (max-width: 992px) {
    .info-container {
        row-gap: 10px;
        flex-wrap: wrap;
    }

    .charts-container {
        width: 100%;
    }

    .chart-row {
        width: 100%;
        flex-direction: column;
        gap: 10px;
        justify-content: center;
        align-items: center;
    }

    .export-btn {
        width: auto;
    }
}
</style>
